<script setup lang="ts">
import { MapContainerAsync as MapContainer } from "@/components/map/MapContainerAsync";
import { MapGeolocationAsync as MapGeolocation } from "@/components/map/MapGeolocationAsync";
import { MapMarkerAsync as MapMarker } from "@/components/map/MapMarkerAsync";
import type { MapPosition, MarkerPopup } from "@/components/map/types";
import { useUpdateHouseHidden } from "@/houses/housesCrud";
import type { House } from "@/houses/types";
import { Erase } from "@iconoir/vue";
import { ref } from "vue";
import { MapAreaEditorAsync as MapAreaEditor } from "./MapAreaEditorAsync";
import MapElectoralDistricts from "./MapElectoralDistricts.vue";
import MapMarkerHouseIcon from "./MapMarkerHouseIcon.vue";
import { coordinatesToMapBounds, isPolygon } from "./mapUtils";
import type { Coordinates } from "./types";

const { houses, defaultMapPosition } = defineProps<{
  houses: Array<House>;
  defaultMapPosition: MapPosition;
}>();

const coordinates = defineModel<Coordinates>({ required: true });

const showDistricts = ref(false);

const calculateMapPosition = () => {
  if (!isPolygon(coordinates.value)) return { ...defaultMapPosition };
  return {
    bounds: coordinatesToMapBounds(coordinates.value),
  };
};
const positionOptions = calculateMapPosition();

const { updateHouseHidden } = useUpdateHouseHidden();
const toggleHouseHidden = (house: House) => {
  updateHouseHidden({ id: house.id, hidden: !house.hidden });
};

const housePopup = (house: House): MarkerPopup => ({
  text: house.houseNumber
    ? `${house.street} ${house.houseNumber}`
    : house.street,
  options: { closeButton: false, closeOnClick: false, offset: 22 },
});
</script>

<template>
  <div class="area-editor">
    <MapContainer v-slot="{ mapInstance }" :options="positionOptions">
      <MapMarker
        v-for="house in houses"
        :key="house.id"
        :lnglat="house.coordinates"
        :map-instance="mapInstance"
        :popup="housePopup(house)"
        popup-trigger="hover"
        @click="toggleHouseHidden(house)"
      >
        <MapMarkerHouseIcon :hidden="house.hidden" class="house-pin" />
      </MapMarker>
      <!-- ensure electoral districts are added before the area editor -->
      <MapElectoralDistricts
        :map-instance="mapInstance"
        :visible="showDistricts"
      />
      <MapAreaEditor v-model="coordinates" :map-instance="mapInstance" />
      <MapGeolocation :map-instance="mapInstance" />
    </MapContainer>
  </div>

  <p v-if="houses.length > 0" class="info-text">
    Haus auf der Karte anklicken, um es aus der Haustüraktion aus- oder
    einzublenden. Ausgeblendete Häuser (hellgrau, mit rotem X) werden in allen
    Haustüraktionen nicht angezeigt.
  </p>
  <div class="row">
    <label for="show-electoral-districts">
      <input
        id="show-electoral-districts"
        v-model="showDistricts"
        type="checkbox"
        name="show-electoral-districts"
      />
      Berliner Wahlkreise anzeigen
    </label>
    <button
      type="reset"
      class="button button--secondary row-self-space-auto"
      @click.prevent="coordinates = []"
    >
      <Erase /> Gebiet neu zeichnen
    </button>
  </div>
</template>

<style scoped>
.area-editor {
  /* MapContainer fills this box (its own `block-size: 100%`). */
  block-size: 30rem;
}

.info-text {
  color: var(--text-color-muted);
}

.house-pin {
  cursor: pointer;
}
</style>
